import React, { Fragment } from 'react';
import { Row, Form } from 'reactstrap';
import { RegisteredInputsBuilder, ApiRequest, ToastBuilder, ButtonBuilder, ModalButtonBuilder, VerticalButtonGroup } from '../../../../../../../../GlobalComponents/Builders';
import FlexInputBuilder from '../../../../../../../../GlobalComponents/Builders/FlexInputBuilder';
import { setInitialFormValues } from '../../../../../../../../GlobalComponents/Helpers';
import useConfirmModal from '../../../../../../../../GlobalComponents/Builders/ConfirmModal';
import ActivityLogViewer from '../shared/ActivityLogViewer';
import RecordStateCacheViewer from './RecordStateCacheViewer';
import IsolatedLogViewer from './IsolatedLogViewer';

/**
 * DMTRecordManage — renders the Queue button + dropdown menu with Activity Log,
 * Record Cache, Isolated Logs, and Manage Record modals for a single record row.
 *
 * Props:
 *   row              - The record row data
 *   resourceId       - The resource_id for this row's resource
 *   jobId            - The job ID
 *   jobData          - Full job data object (needs is_sync)
 *   statusOptions    - Array of status options for the manage form
 *   form             - { register, errors, control, setValue, handleSubmit, getValues }
 *   handlers         - { handleDMTAction, handleManageSyncRecord, onRecordSearch }
 *   cacheModal       - { setCacheKeyOptions, setSelectedCacheKeys, setCacheKeysContext, setCacheKeysModal }
 */
const DMTRecordManage = ({ row, resourceId, jobId, jobData, isMobile = false, statusOptions, form, handlers, cacheModal }) => {

    const { register, errors, control, setValue, handleSubmit, getValues } = form;
    const { handleDMTAction, handleManageSyncRecord, onRecordSearch } = handlers;
    const { setCacheKeyOptions, setSelectedCacheKeys, setCacheKeysContext, setCacheKeysModal } = cacheModal;

    const { confirm, ConfirmModal } = useConfirmModal();

    const queueModal = <ModalButtonBuilder
        title='Process'
        modalTitle='Queue Record'
        className='btn-outline-danger btn-sm m-0 px-4'
        formId='queue-record'
        onClick={() => { setInitialFormValues(row, setValue) }}
        body={
            <Form id='queue-record' onSubmit={
                handleSubmit(formData => {
                    let actionData = {
                        jobId, resourceId: resourceId,
                        overrideLock: !!formData.overrideLock,
                        fetchUpdates: !!formData.fetchUpdates,
                        dryRun: formData.dryRun === 'true',
                        ignoreTerminalState: !!formData.ignoreTerminalState,
                        isolateLogs: !!formData.isolateLogs,
                        valuesToQueue: [String(formData.sourceRecId)],
                        limit: 1
                    }
                    handleDMTAction('queue-resource', actionData, null, true)
                })}>
                <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>Please double check resource mappings on the Job Page before proceeding to process record : <strong className='txt-danger'>{row.source_rec_id}</strong></p>
                <Row>
                    <RegisteredInputsBuilder
                        registrator={register}
                        registratorErrors={errors}
                        control={control}
                        inputs={[
                            {
                                label: 'Commit',
                                name: 'dryRun',
                                type: 'reactselect',
                                size: 11,
                                inputSize: 12,
                                helpText: 'Perform live updates or a Dry Test Run',
                                options: [
                                    { label: 'No, do not make updates', value: 'true' },
                                    { label: 'Yes, create/update resources', value: 'false' },
                                ],
                                settings: { topAligned: true, helpTextOffset: 0, treatNullAsEmpty: true },
                            },
                            {
                                label: 'Record',
                                name: 'sourceRecId',
                                type: 'static',
                                size: 12,
                                inputSize: 7,
                                visible: false
                            },
                        ]} />
                </Row>
                <FlexInputBuilder
                    form={{ register }}
                    style={{ marginTop: 8 }}
                    inputs={[
                        { name: 'overrideLock', label: 'Override Lock', type: 'checkbox', size: 6 },
                        { name: 'fetchUpdates', label: 'Fetch Updates', type: 'checkbox', size: 6 },
                        { name: 'ignoreTerminalState', label: 'Ignore Terminal State', type: 'checkbox', size: 6 },
                        { name: 'isolateLogs', label: 'Isolate Logs', type: 'checkbox', size: 6 },
                    ]}
                />
            </Form>
        }
    />;

    return (
        <Fragment>
        <div className='btn-group my-1' style={{ gap: 0 }}>
            {queueModal}
            <VerticalButtonGroup
                label='☰'
                className='btn-danger btn-sm m-0'
                style={{ minWidth: 0, width: 35, padding: 0, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                settings={{ speedFactor: 0.15, hideArrow: true }}
                dropdownStyle={{ width: 'auto', minWidth: 155, position: 'left' }}
                buttons={[
                    <ModalButtonBuilder
                        title='Activity Log'
                        className='btn-outline-secondary px-3'
                        onSubmit={false}
                        onCancelLabel='Close'
                        size='lg'
                        body={
                            <ActivityLogViewer
                                jobId={jobId}
                                resourceId={resourceId}
                                sourceRecId={row.source_rec_id}
                            />
                        }
                    />,
                    <ModalButtonBuilder
                        title='Record Cache'
                        className='btn-outline-secondary px-3'
                        onSubmit={false}
                        onCancelLabel='Close'
                        size='lg'
                        body={
                            <RecordStateCacheViewer
                                jobId={jobId}
                                resourceId={resourceId}
                                sourceRecId={row.source_rec_id}
                                isSync={!!jobData.is_sync}
                                autoLoad={true}
                                allowClear={true}
                            />
                        }
                    />,
                    <ModalButtonBuilder
                        title='Isolated Logs'
                        className='btn-outline-info px-3'
                        onSubmit={false}
                        onCancelLabel='Close'
                        size='xl'
                        body={
                            <IsolatedLogViewer
                                jobId={jobId}
                                resourceId={resourceId}
                                sourceRecId={row.source_rec_id}
                            />
                        }
                    />,
                    <ModalButtonBuilder
                        title='Manage Record'
                        className='btn-outline-danger px-3'
                        formId='manage-sync-record'
                        size='lg'
                        onClick={() => {
                            setValue('manageRecId', row.source_rec_id);
                            setValue('manageStatus', '');
                            setValue('manageResourceData', row.resource_data ? JSON.stringify(row.resource_data, null, 2) : '');
                            setValue('manageDestRecId', '');
                            setValue('manageClearCache', '');
                            setValue('manageClearLog', '');
                        }}
                        body={
                            <Form id='manage-sync-record' onSubmit={
                                handleSubmit(formData => {
                                    if (formData.manageResourceData && formData.manageResourceData.trim() !== '') {
                                        try { JSON.parse(formData.manageResourceData); } catch { return ToastBuilder('error', 'Invalid JSON in resource data'); }
                                    }
                                    handleManageSyncRecord(resourceId, formData.manageRecId, formData);
                                })}>
                                <p>Manage record: <strong style={{ color: 'red' }}>{row.source_rec_id}</strong></p>
                                <p className='text-muted' style={{ fontSize: 12 }}>Only fields you explicitly set will be updated. Empty/unselected fields are ignored.</p>
                                <hr/>
                                <Row>
                                <RegisteredInputsBuilder
                                    registrator={register}
                                    registratorErrors={errors}
                                    control={control}
                                    inputs={[
                                        { label: 'Set Status', name: 'manageStatus', type: 'reactselect', size: 4, inputSize: 12, placeholder: 'Leave empty to skip', options: statusOptions.filter(o => o.value !== 'All'), settings: { topAligned: true, isClearable: true } },
                                        { label: 'Clear State Cache', name: 'manageClearCache', type: 'reactselect', size: 4, inputSize: 12, placeholder: 'Leave empty to skip', options: [{ label: 'Yes, clear cached state', value: 'true' }], settings: { topAligned: true, isClearable: true } },
                                        { label: 'Clear Activity Log', name: 'manageClearLog', type: 'reactselect', size: 4, inputSize: 12, placeholder: 'Leave empty to skip', options: [{ label: 'Delete all', value: 'delete-all' }, { label: 'Keep 5 most recent', value: '5' }, { label: 'Keep 10 most recent', value: '10' }, { label: 'Keep 15 most recent', value: '15' }, { label: 'Keep 20 most recent', value: '20' }], settings: { topAligned: true, isClearable: true } },
                                        { label: 'Set Resource Data', name: 'manageResourceData', type: 'textarea', size: 12, inputSize: 12, placeholder: 'Paste JSON — leave empty to skip', style: { height: 150, fontFamily: 'monospace', fontSize: 12 }, settings: { topAligned: true } },
                                        { label: 'Set Destination Rec ID', name: 'manageDestRecId', type: 'text', size: 12, inputSize: 5, placeholder: 'Type "unset" to clear, or enter ID', helpText: 'Type "unset" to set destination to 0. Also clears record mappings.', settings: { topAligned: true, helpTextOffset: 1 } },
                                        { label: 'Record', name: 'manageRecId', type: 'static', visible: false },
                                    ]} />
                                </Row>
                                <div style={{ borderTop: '1px solid #eee', marginTop: 12, paddingTop: 12 }}>
                                    <div className='d-flex align-items-center justify-content-between'>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#6c757d' }}>State Cache</span>
                                        <div className='d-flex gap-2'>
                                            <ButtonBuilder form='' label='Fetch Source Record' className='btn-outline-info btn-sm' style={{ fontSize: 11 }}
                                                onClick={async () => {
                                                    const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
                                                        pathParams: { jobId, actionName: 'fetch-source-record' },
                                                        body: { sourceResourceId: resourceId, formData: { sourceRecId: parseInt(row.source_rec_id), isSync: !!jobData.is_sync } },
                                                    });
                                                    if (res.success) {
                                                        ToastBuilder('success', res.data?.message || 'Record fetched', {}, 5000);
                                                        onRecordSearch(getValues(), true);
                                                    } else {
                                                        ToastBuilder('error', res.error || 'Failed to fetch record');
                                                    }
                                                }} useSpinner={false} />
                                            <ButtonBuilder form='' label='Record Resources Count' className='btn-outline-secondary btn-sm' style={{ fontSize: 11 }}
                                                onClick={async () => {
                                                    const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
                                                        pathParams: { jobId, actionName: 'count-record-resources' },
                                                        body: { sourceResourceId: resourceId, formData: { sourceRecId: parseInt(row.source_rec_id) } },
                                                    });
                                                    if (res.success) {
                                                        ToastBuilder('success', `${res.data?.count ?? 0} record resource(s)`, {}, 5000);
                                                    } else {
                                                        ToastBuilder('error', res.error || 'Failed to count record resources');
                                                    }
                                                }} useSpinner={false} />
                                        </div>
                                    </div>
                                    <div style={{ marginTop: 8 }}>
                                        <RecordStateCacheViewer
                                            jobId={jobId}
                                            resourceId={resourceId}
                                            sourceRecId={row.source_rec_id}
                                            isSync={!!jobData.is_sync}
                                            compact={true}
                                            allowClear={true}
                                        />
                                    </div>
                                </div>
                                <div style={{ background: 'rgba(220,53,69,0.05)', border: '1px solid rgba(220,53,69,0.2)', borderRadius: 6, padding: '12px 14px', marginTop: 16 }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#dc3545' }}>Danger Zone</span>

                                    {/* Tier 1 — Partial resets (targeted, one slice each) */}
                                    <div style={{ marginTop: 10 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#6c757d', display: 'block' }}>Clear</span>
                                        <span style={{ fontSize: 10, color: '#6c757d', display: 'block', marginBottom: 6 }}>Remove a single slice of the record&apos;s state. Leaves the record and its destination link in place.</span>
                                        <div className='d-flex gap-2 flex-wrap'>
                                            <ButtonBuilder form='' label='Clear Cache' className='btn-outline-secondary btn-sm' style={{ fontSize: 11 }}
                                                onClick={async () => {
                                                    const cacheRes = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
                                                        pathParams: { jobId, actionName: 'view-state-cache' },
                                                        body: { sourceResourceId: resourceId, formData: { sourceRecId: parseInt(row.source_rec_id) } },
                                                    });
                                                    if (!cacheRes.success || !cacheRes.data?.cache?.current_state) { ToastBuilder('info', 'No state cache found for this record'); return; }
                                                    const keys = Object.keys(cacheRes.data.cache.current_state);
                                                    setCacheKeyOptions(keys.map(k => ({ label: k, value: k })));
                                                    setSelectedCacheKeys([]);
                                                    setCacheKeysContext({ resourceId: resourceId, sourceRecId: row.source_rec_id });
                                                    setCacheKeysModal(true);
                                                }} useSpinner={false} />
                                            <ButtonBuilder form='' label='Clear Secondary Requests' className='btn-outline-secondary btn-sm' style={{ fontSize: 11 }}
                                                onClick={async () => {
                                                    const confirmed = await confirm(`Clear secondary requests for record ${row.source_rec_id}? This removes queued side-effect requests. Cannot be undone.`, { title: 'Clear Secondary Requests', confirmLabel: 'Clear' });
                                                    if (!confirmed) return;
                                                    const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
                                                        pathParams: { jobId, actionName: 'clear-secondary-requests' },
                                                        body: { sourceResourceId: resourceId, formData: { sourceRecId: parseInt(row.source_rec_id), isSync: !!jobData.is_sync } },
                                                    });
                                                    if (res.success) { ToastBuilder('success', res.data?.message || 'Secondary requests cleared'); onRecordSearch(getValues(), true); }
                                                    else { ToastBuilder('error', res.error || 'Failed to clear secondary requests'); }
                                                }} useSpinner={false} />
                                            <ButtonBuilder form='' label='Clear Record Resources' className='btn-outline-secondary btn-sm' style={{ fontSize: 11 }}
                                                onClick={async () => {
                                                    const confirmed = await confirm(`Clear record resources for record ${row.source_rec_id}? This removes the per-resource tracking rows. Cannot be undone.`, { title: 'Clear Record Resources', confirmLabel: 'Clear' });
                                                    if (!confirmed) return;
                                                    const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
                                                        pathParams: { jobId, actionName: 'clear-record-resources' },
                                                        body: { sourceResourceId: resourceId, formData: { sourceRecId: parseInt(row.source_rec_id) } },
                                                    });
                                                    if (res.success) { ToastBuilder('success', res.data?.message || 'Record resources cleared'); }
                                                    else { ToastBuilder('error', res.error || 'Failed to clear record resources'); }
                                                }} useSpinner={false} />
                                        </div>
                                    </div>

                                    {/* Tier 2 — Re-initialize (combined reset, record kept) */}
                                    <div style={{ marginTop: 12 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#dc3545', display: 'block' }}>Re-initialize</span>
                                        <span style={{ fontSize: 10, color: '#6c757d', display: 'block', marginBottom: 6 }}>Unsets the destination + record mappings and clears secondary requests &amp; record resources, so the next run re-creates it. Keeps the row.</span>
                                        <div className='d-flex gap-2 flex-wrap'>
                                            <ButtonBuilder form='' label='Fresh Start' className='btn-secondary btn-sm' style={{ fontSize: 11 }}
                                                onClick={async () => {
                                                    const confirmed = await confirm(`Fresh start record ${row.source_rec_id}? Unsets destination, deletes record mappings, clears secondary requests + record resources. Keeps the state cache. Cannot be undone.`, { title: 'Fresh Start', confirmLabel: 'Fresh Start' });
                                                    if (!confirmed) return;
                                                    const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
                                                        pathParams: { jobId, actionName: 'fresh-start' },
                                                        body: { sourceResourceId: resourceId, formData: { sourceRecId: parseInt(row.source_rec_id), isSync: !!jobData.is_sync } },
                                                    });
                                                    if (res.success) { ToastBuilder('success', res.data?.message || 'Fresh start complete'); onRecordSearch(getValues(), true); }
                                                    else { ToastBuilder('error', res.error || 'Failed to fresh start'); }
                                                }} useSpinner={false} />
                                            <ButtonBuilder form='' label='Fresh Start + All Cache' className='btn-secondary btn-sm' style={{ fontSize: 11 }}
                                                onClick={async () => {
                                                    const confirmed = await confirm(`Fresh start record ${row.source_rec_id} and also clear its state cache? Unsets destination, deletes record mappings, clears secondary requests, record resources + state cache. Cannot be undone.`, { title: 'Fresh Start + All Cache', confirmLabel: 'Fresh Start + All Cache' });
                                                    if (!confirmed) return;
                                                    const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
                                                        pathParams: { jobId, actionName: 'fresh-start' },
                                                        body: { sourceResourceId: resourceId, formData: { sourceRecId: parseInt(row.source_rec_id), isSync: !!jobData.is_sync, clearCache: true } },
                                                    });
                                                    if (res.success) { ToastBuilder('success', res.data?.message || 'Fresh start complete'); onRecordSearch(getValues(), true); }
                                                    else { ToastBuilder('error', res.error || 'Failed to fresh start'); }
                                                }} useSpinner={false} />
                                        </div>
                                    </div>

                                    {/* Tier 3 — Destroy (irreversible removal) */}
                                    <div style={{ marginTop: 12, borderTop: '1px solid rgba(220,53,69,0.2)', paddingTop: 10 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#dc3545', display: 'block' }}>Delete</span>
                                        <span style={{ fontSize: 10, color: '#6c757d', display: 'block', marginBottom: 6 }}>Permanently removes the record, its mappings, activity log and state cache.</span>
                                        <div className='d-flex gap-2 flex-wrap'>
                                            <ButtonBuilder form='' label='Delete Record' className='btn-danger btn-sm' style={{ fontSize: 11 }}
                                                onClick={async () => {
                                                    const confirmed = await confirm(`Permanently delete record ${row.source_rec_id}? Removes the record, mappings, activity log and state cache. Cannot be undone.`, { title: 'Delete Record', confirmLabel: 'Delete' });
                                                    if (!confirmed) return;
                                                    const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
                                                        pathParams: { jobId, actionName: 'delete-record' },
                                                        body: { sourceResourceId: resourceId, formData: { sourceRecId: parseInt(row.source_rec_id), isSync: !!jobData.is_sync } },
                                                    });
                                                    if (res.success) { ToastBuilder('success', res.data?.message || 'Record deleted'); onRecordSearch(getValues(), true); }
                                                    else { ToastBuilder('error', res.error || 'Failed to delete record'); }
                                                }} useSpinner={false} />
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        }
                    />,
                ]}
            />
        </div>
        <ConfirmModal />
        </Fragment>
    );
};

export default DMTRecordManage;
